import React from 'react';
import classnames from 'classnames';
import { Loader, LoaderProps } from '../loader';
import { Icon, IconProps } from '../icon';
import { Size } from '../types';

import styles from './text-button.module.scss';

type LoadingType = { loading?: { isLoading: boolean } & Omit<LoaderProps, 'size'> };

type HTMLButtonProps = Pick<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'id' | 'type' | 'disabled' | 'onClick' | 'className' | 'autoFocus' | 'onContextMenu'
>;

export type TextButtonProps = {
  variant: 'primary' | 'secondary' | 'hyperlink' | 'ghost' | 'secondary-on-media';
  children: React.ReactNode;
  size?: Size;
  iconPosition?: 'left' | 'right';
  iconName?: IconProps['name'];
  isFullWidth?: boolean;
} & HTMLButtonProps &
  LoadingType;

const baseClass = 'text-button';

export const TextButton: React.FC<TextButtonProps> = ({
  id,
  variant = 'primary',
  size = '300',
  type = 'button',
  iconPosition = 'left',
  iconName,
  disabled,
  className,
  onClick,
  children,
  isFullWidth,
  loading,
  autoFocus,
  onContextMenu
}) => {
  const classes = classnames(
    styles[baseClass],
    styles[`${baseClass}--${variant}`],
    { [styles[`${baseClass}--${size}`]]: size },
    { [styles[`${baseClass}--full-width`]]: isFullWidth },
    { [styles[`${baseClass}--icon-${iconPosition}`]]: iconPosition },
    className
  );

  const getIcon = () => {
    if (loading?.isLoading) {
      return (
        <Loader
          size="small"
          hiddenLoadingStatus={loading.hiddenLoadingStatus}
          messageId={loading.messageId}
          className={styles[`${baseClass}__loader`]}
        />
      );
    }

    if (iconName) {
      return <Icon name={iconName} className={styles[`${baseClass}__icon`]} aria-hidden />;
    }
  };

  return (
    <button
      id={id}
      data-testid="button-component"
      className={classes}
      type={type}
      disabled={loading?.isLoading ? true : disabled}
      onClick={onClick}
      autoFocus={autoFocus}
      onContextMenu={onContextMenu}
    >
      {getIcon()}
      <span className={styles[`${baseClass}__text`]}>{children}</span>
    </button>
  );
};
